import {create} from "zustand";
import {createJSONStorage, persist} from "zustand/middleware";
import {ScreenType} from "./types/screenType";
import {StoreState} from "@/store/types/storeState";
import {StoreAction} from "@/store/types/storeAction";
import {RegionOptions} from "@/store/types/RegionOptions";
import {MtpFilters} from "@/store/types/MtpFilters";

export const getDefaultMtpFilters = (): MtpFilters => ({
    searchText: undefined,
    regionId: undefined,
    settlementId: undefined,
    pageNumber: 1,
    includeUnavailablePlaces: false,
    freeAccommodation: undefined,
    paidAccommodation: undefined,
    hasWc: false,
    showerOrBathtubAvailable: false,
    kitchenAvailable: false,
    workingElevatorAvailable: false,
    familyRooms: false,
    maleRooms: false,
    femaleRooms: false,
    spaceAvailable: false,
    petsAvailable: false,
    disabledOrReducedMobilityAvailable: false,
    bunkBedsAvailable: false,
});
const getInitialState = (): StoreState => {
    return {
        useIncreasedContrastTheme: false,
        screenType: ScreenType.Desktop,
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isSidebarOpen: false,
        isLoginOrRegistrationModalVisible: false,
        loading: false,
        mtpFilters: getDefaultMtpFilters(),
        availableSpacesRegionId: undefined,
        needReloadMtpData: false,
        regionOptions: [],
        showReserveModal: false,
        idToShowInReserveModal: null,
    };
};

export const useStore = create<StoreState & StoreAction>()(
    persist((set) => ({
        ...getInitialState(),
        clearStore: () => set(() => (getInitialState())),
        setScreenType: (value: ScreenType) => set(() => ({ 
            screenType: value,
            isMobile: value === ScreenType.Mobile,
            isTablet: value === ScreenType.Tablet,
            isDesktop: value === ScreenType.Desktop
        })),
        changeTheme: () => 
            set((state) => ({useIncreasedContrastTheme: !state.useIncreasedContrastTheme })),
        showSidebar: () => 
            set((state) => ({isSidebarOpen: true })),
        hideSidebar: () =>
            set((state) => ({isSidebarOpen: false })),
        showLoginOrRegistrationModal: (visible: boolean) => 
            set(() => ({ isLoginOrRegistrationModalVisible: visible })),
        setLoading: (value: boolean) => set(() => ({ loading: value })),
        updateMtpFilters: (value: MtpFilters) => set(() => ({
            mtpFilters: value,
            availableSpacesRegionId: value.regionId,
        })),
        patchMtpFilters: (value: Partial<MtpFilters>) =>
            set((state) => ({
                mtpFilters: {
                    ...state.mtpFilters,
                    pageNumber: 1,
                    ...value,
                },
                availableSpacesRegionId: Object.prototype.hasOwnProperty.call(value, 'regionId')
                    ? value.regionId
                    : state.availableSpacesRegionId,
                needReloadMtpData: true,
            })),
        clearMtpFilters: () => set(() => ({ 
            mtpFilters: getDefaultMtpFilters(),
            availableSpacesRegionId: undefined,
            needReloadMtpData: true,
        })),
        clearNeedReloadMtpData: () => set(() => ({needReloadMtpData: false})), 
        setAvailableSpacesRegionId: (regionId?: number) => set(() => ({ availableSpacesRegionId: regionId })),
        saveRegionOptions: (regions: RegionOptions[]) => set(() => ({ regionOptions: regions })),
        setShowReserveModal: (visible: boolean) => set(() => ({ showReserveModal: visible })),
        setIdToShowInReserveModal: (id: number | null) => set(() => ({ idToShowInReserveModal: id })),
}), {
        name: "vpo-store",
        storage: createJSONStorage(() => localStorage),
        // middleware де вказуємо поля для збереження у storage
        partialize: (state) => ({
            useIncreasedContrastTheme: state.useIncreasedContrastTheme,
            screenType: state.screenType,
            isMobile: state.isMobile,
            isTablet: state.isTablet,
            isDesktop: state.isDesktop,
            isSidebarOpen: state.isSidebarOpen,
            isLoginOrRegistrationModalVisible: state.isLoginOrRegistrationModalVisible,
            loading: state.loading,
            regionOptions: state.regionOptions,
            showReserveModal: state.showReserveModal,
            idToShowInReserveModal: state.idToShowInReserveModal,
        }),
    })
);
